// Utility functions for text highlighting

export const HIGHLIGHT_COLORS = {
  yellow: {
    class: 'highlight-yellow',
    color: '#fef08a',
    border: '#eab308',
    name: 'Yellow'
  },
  green: {
    class: 'highlight-green',
    color: '#bbf7d0',
    border: '#22c55e',
    name: 'Green'
  },
  blue: {
    class: 'highlight-blue',
    color: '#bfdbfe',
    border: '#3b82f6',
    name: 'Blue'
  },
  pink: {
    class: 'highlight-pink',
    color: '#fecdd3',
    border: '#ec4899',
    name: 'Pink'
  },
  orange: {
    class: 'highlight-orange',
    color: '#fed7aa',
    border: '#f97316',
    name: 'Orange'
  }
};

/**
 * Apply highlighting to selected text
 * @param {string} text - The text to highlight
 * @param {string} colorKey - The color key from HIGHLIGHT_COLORS
 * @returns {string} - The highlighted text
 */
export const applyHighlight = (text, colorKey) => {
  const color = HIGHLIGHT_COLORS[colorKey];
  if (!color) {
    console.warn(`Unknown highlight color: ${colorKey}`);
    return text;
  }
  
  return `<span class="${color.class}">${text}</span>`;
};

/**
 * Remove highlighting from text
 * @param {string} text - The text to clean
 * @returns {string} - The text without highlighting
 */
export const removeHighlight = (text) => {
  return text.replace(/<span class="highlight-[^"]*">([^<]*)<\/span>/g, '$1');
};

/**
 * Check if text contains highlighting
 * @param {string} text - The text to check
 * @returns {boolean} - True if text contains highlighting
 */
export const hasHighlighting = (text) => {
  return /<span class="highlight-[^"]*">/.test(text);
};

/**
 * Get all highlighted text segments
 * @param {string} text - The text to parse
 * @returns {Array} - Array of highlighted segments with their colors
 */
export const getHighlightedSegments = (text) => {
  const segments = [];
  const regex = /<span class="(highlight-[^"]*)">([^<]*)<\/span>/g;
  let match;
  
  while ((match = regex.exec(text)) !== null) {
    segments.push({
      text: match[2],
      colorClass: match[1],
      startIndex: match.index,
      endIndex: match.index + match[0].length
    });
  }
  
  return segments;
};

/**
 * Convert markdown highlighting to HTML
 * @param {string} markdown - Markdown text with highlighting
 * @returns {string} - HTML with proper highlighting
 */
export const markdownToHighlightedHTML = (markdown) => {
  // This function would convert markdown highlighting syntax to HTML
  // For now, we'll just return the markdown as-is since we're using HTML spans
  return markdown;
};

/**
 * Convert HTML highlighting to markdown
 * @param {string} html - HTML text with highlighting
 * @returns {string} - Markdown with highlighting syntax
 */
export const highlightedHTMLToMarkdown = (html) => {
  // Convert HTML highlighting back to markdown if needed
  return html.replace(/<span class="highlight-[^"]*">([^<]*)<\/span>/g, '==$1==');
};

/**
 * Create a highlight command for MDEditor
 * @param {string} colorKey - The color key
 * @param {string} shortcut - Keyboard shortcut
 * @returns {Object} - MDEditor command object
 */
export const createHighlightCommand = (colorKey, shortcut) => {
  const color = HIGHLIGHT_COLORS[colorKey];
  if (!color) {
    throw new Error(`Unknown highlight color: ${colorKey}`);
  }
  
  return {
    name: `${colorKey}Highlight`,
    keyCommand: `${colorKey}Highlight`,
    buttonProps: { 'aria-label': `Add ${color.name.toLowerCase()} highlight (${shortcut})` },
    icon: (
      <div 
        style={{ 
          width: 12, 
          height: 12, 
          backgroundColor: color.color, 
          borderRadius: 2, 
          border: `1px solid ${color.border}` 
        }}
      />
    ),
    execute: (state, api) => {
      const selection = state.selectedText;
      if (selection) {
        api.replaceSelection(applyHighlight(selection, colorKey));
      } else {
        api.replaceSelection(applyHighlight('highlighted text', colorKey));
      }
    },
    shortcutKeys: shortcut
  };
};

/**
 * Create all highlight commands
 * @returns {Array} - Array of all highlight commands
 */
export const createAllHighlightCommands = () => {
  return [
    createHighlightCommand('yellow', 'ctrl+shift+y'),
    createHighlightCommand('green', 'ctrl+shift+g'),
    createHighlightCommand('blue', 'ctrl+shift+b'),
    createHighlightCommand('pink', 'ctrl+shift+p'),
    createHighlightCommand('orange', 'ctrl+shift+o'),
    {
      name: 'removeHighlight',
      keyCommand: 'removeHighlight',
      buttonProps: { 'aria-label': 'Remove highlight (Ctrl+Shift+R)' },
      icon: (
        <div 
          style={{ 
            width: 12, 
            height: 12, 
            backgroundColor: '#f3f4f6', 
            borderRadius: 2, 
            border: '1px solid #9ca3af', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            fontSize: '8px' 
          }}
        >
          ×
        </div>
      ),
      execute: (state, api) => {
        const selection = state.selectedText;
        if (selection) {
          api.replaceSelection(removeHighlight(selection));
        }
      },
      shortcutKeys: 'ctrl+shift+r'
    }
  ];
};

/**
 * Get highlight color by class name
 * @param {string} className - The CSS class name
 * @returns {Object|null} - The color object or null if not found
 */
export const getHighlightColorByClass = (className) => {
  return Object.values(HIGHLIGHT_COLORS).find(color => color.class === className) || null;
};

/**
 * Validate highlight color
 * @param {string} colorKey - The color key to validate
 * @returns {boolean} - True if valid
 */
export const isValidHighlightColor = (colorKey) => {
  return colorKey in HIGHLIGHT_COLORS;
}; 